import { FC, DetailedHTMLProps, TextareaHTMLAttributes } from 'react';
import Label from 'src/common/components/Label';
import { Field, useField } from 'react-final-form';
import Typography from 'src/common/components/Typography';
import { withSuffix } from 'src/common/utils/dataAttributeHelpers';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import styles from './TextArea.module.scss';
import Icon from '../IconDS22';
import Tooltip from '../Tooltip';

export type Props =
  Omit<DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>, 'ref'> &
  {
    label: string;
    optionalLabel?: string;
    'data-test'?: string;
    rightSlot?: React.ReactNode;
    /** False keeps red error style, but omits the error message and icon below field */
    showErrorBelow?: boolean;
    /** To avoid red outline on init (for forms that use Form level validation) */
    showErrorOnlyIfTouched?: boolean;
    toolTipContent?: React.ReactNode;
    rows?: number;
    /** text length limit */
    maxLength?: number;
    parse?: (value: string) => string;
  };

const TextArea: FC<Props> = ({
  name = '',
  label,
  optionalLabel,
  placeholder,
  disabled,
  'data-test': dataTest,
  rightSlot,
  showErrorBelow = true,
  showErrorOnlyIfTouched,
  toolTipContent,
  rows = 5,
  maxLength = 1500,
  parse,
  ...props
}) => {
  const { meta: { error, touched } } = useField(name);
  const hasError = showErrorOnlyIfTouched ? touched && error : error;
  return (
    <div className={styles.root}>
      <div className={styles.label}>
        <Label
          htmlFor={name}
          label={label}
          size="m"
          inlineSlot={optionalLabel && (
            <Typography
              type="span"
              size="body-m"
            >
              {`(${optionalLabel})`}
            </Typography>
          )}
          rightSlot={rightSlot}
        />
        {toolTipContent && (
          <Tooltip>
            {toolTipContent}
          </Tooltip>
        )}
      </div>
      <Field
        name={name}
        title={label}
        component="textarea"
        maxLength={maxLength}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        className={getClassNames(styles, 'field', classNames({ hasError }))}
        data-test={withSuffix(dataTest, 'textarea')}
        parse={parse}
        {...props}
      />
      {showErrorBelow && hasError && (
        <div className={styles.error}>
          <Icon id="warning-filled" size="s" />
          {error}
        </div>
      )}
    </div>
  );
};

export default TextArea;
